import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { 
  Users, Copy, Check, Info, Upload, Award, Loader2, Sparkles, 
  CheckCircle2, ChevronRight, X, AlertTriangle, AlertCircle, RefreshCw, Wallet 
} from 'lucide-react';
import Sidebar from '../components/Sidebar';
import TopBar from '../components/TopBar';
import { useCexAccount } from '../context/CexAccountContext';
import { usePortfolio } from '../context/PortfolioContext';
import { useTranslation } from 'react-i18next';
import { API_BASE_URL } from '../config/api';

interface CommissionLog {
  id: number;
  date: string;
  referred_user_name: string;
  referred_user_email: string;
  amount_idr: number;
  status: 'pending' | 'earned' | 'paid' | 'cancelled';
  paid_at: string | null;
}

interface PartnershipData {
  referral_code: string;
  is_community: boolean;
  discount_percent: number;
  commission_percent: number;
  referral_link: string;
  show_community_upgrade_prompt: boolean;
  summary: {
    total_referrals: number;
    active_subscribers: number;
    total_commission_idr: number;
    unpaid_commission_idr: number;
    tier: string;
  };
}

const ReferralPage: React.FC = () => {
  const { t } = useTranslation();
  const { userName } = usePortfolio();
  const cexContext = useCexAccount();
  const { selectedAccount, setSelectedAccount } = cexContext;

  // Page state: 'loading' | 'apply_pre' | 'apply_form' | 'pending' | 'rejected' | 'dashboard'
  const [pageState, setPageState] = useState<'loading' | 'apply_pre' | 'apply_form' | 'pending' | 'rejected' | 'dashboard'>('loading');
  const [partnershipData, setPartnershipData] = useState<PartnershipData | null>(null);
  const [commissions, setCommissions] = useState<CommissionLog[]>([]);
  const [loadingAction, setLoadingAction] = useState(false);
  const [error, setError] = useState('');
  const [successMsg, setSuccessMsg] = useState('');

  // Apply Form State
  const [partnerType, setPartnerType] = useState<'influencer' | 'community'>('community');
  const [communityName, setCommunityName] = useState('');
  const [communityLink, setCommunityLink] = useState('');
  const [applyReason, setApplyReason] = useState('');
  const [notifPref, setNotifPref] = useState<'email' | 'telegram'>('email');

  // Withdrawal Modal State
  const [showWithdrawModal, setShowWithdrawModal] = useState(false);
  const [withdrawEmail, setWithdrawEmail] = useState('');
  const [withdrawProvider, setWithdrawProvider] = useState('BCA');
  const [withdrawAccount, setWithdrawAccount] = useState('');
  const [disclaimerChecked, setDisclaimerChecked] = useState(false);
  
  // KTP Verification State
  const [ktpVerified, setKtpVerified] = useState(false);
  const [isVerifyingKtp, setIsVerifyingKtp] = useState(false);
  const [ktpError, setKtpError] = useState('');
  const [ktpSuccessMsg, setKtpSuccessMsg] = useState('');
  const [expectedName, setExpectedName] = useState('');

  // Link copy state
  const [copiedLink, setCopiedLink] = useState(false);
  const [copiedCode, setCopiedCode] = useState(false);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    setPageState('loading');
    setError('');
    try {
      const token = localStorage.getItem('auth_token');
      const res = await fetch(`${API_BASE_URL}/api/partnership/dashboard`, {
        headers: {
          'Accept': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });
      
      const data = await res.json();
      
      if (res.status === 404 || (res.ok && !data.success)) {
        // Assume no partnership or need to apply
        setPageState('apply_pre');
      } else if (res.ok && data.success && data.data) {
        setPartnershipData(data.data);
        setPageState('dashboard');
        // Fetch commissions if partner
        fetchCommissions();
      } else if (data.application_status === 'pending') {
        setPageState('pending');
      } else if (data.application_status === 'rejected') {
        setPageState('rejected');
      } else {
        setPageState('apply_pre');
      }
    } catch (err) {
      setError('Gagal memuat data kemitraan. Pastikan server aktif.');
      setPageState('apply_pre'); // Fallback to apply pre-form
    }
  };

  const fetchCommissions = async () => {
    try {
      const token = localStorage.getItem('auth_token');
      const res = await fetch(`${API_BASE_URL}/api/partnership/commissions`, {
        headers: {
          'Accept': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });
      const data = await res.json();
      if (res.ok && data.success) {
        setCommissions(data.data || []);
      }
    } catch (err) {
      console.error('Failed to fetch commissions:', err);
    }
  };

  const handleApplySubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!communityName || !communityLink) {
      setError('Harap isi semua kolom wajib.');
      return;
    }
    if (!communityLink.startsWith('https://')) {
      setError('Tautan komunitas wajib diawali dengan https://');
      return;
    }
    setLoadingAction(true);
    setError('');
    
    try {
      const token = localStorage.getItem('auth_token');
      const email = localStorage.getItem('user_email') || '';
      const res = await fetch(`${API_BASE_URL}/api/partnership/apply`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          type: partnerType,
          email: email,
          community_name: communityName,
          community_link: communityLink,
          reason: applyReason || undefined,
          notification_pref: notifPref
        })
      });
      const data = await res.json();
      if (res.ok) {
        setPageState('pending');
        setSuccessMsg('Pengajuan kemitraan berhasil dikirim!');
      } else {
        setError(data.message || 'Gagal mengirim pengajuan kemitraan.');
      }
    } catch (err) {
      setError('Gagal terhubung ke server.');
    } finally {
      setLoadingAction(false);
    }
  };

  const handleKtpUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!expectedName.trim()) {
      setKtpError('Harap isi nama pemilik rekening terlebih dahulu untuk pencocokan KTP.');
      e.target.value = '';
      return;
    }

    setIsVerifyingKtp(true);
    setKtpError('');
    setKtpSuccessMsg('');
    setKtpVerified(false);

    try {
      // Convert to base64
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = async () => {
        const base64String = reader.result as string;

        try {
          const token = localStorage.getItem('auth_token');
          const res = await fetch(`${API_BASE_URL}/api/partnership/withdraw/verify-ktp`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Accept': 'application/json',
              'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify({
              ktp_image_base64: base64String,
              expected_name: expectedName.trim()
            })
          });

          const data = await res.json();
          if (res.ok && data.success && data.is_match) {
            setKtpVerified(true);
            setKtpSuccessMsg(data.reason || 'KTP berhasil diverifikasi! Nama cocok.');
          } else {
            setKtpError(data.reason || 'Nama pada KTP tidak cocok dengan nama rekening.');
          }
        } catch (err) {
          setKtpError('Gagal memproses verifikasi KTP.');
        } finally {
          setIsVerifyingKtp(false);
          // Zero-Storage Compliance: state RAM dibersihkan segera setelah proses selesai
          // base64String tidak disimpan di state persisten
          e.target.value = '';
        }
      };
      reader.onerror = () => {
        setKtpError('Gagal membaca file gambar.');
        setIsVerifyingKtp(false);
      };
    } catch (err) {
      setKtpError('Terjadi kesalahan saat memproses gambar.');
      setIsVerifyingKtp(false);
    }
  };

  const handleWithdrawSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!withdrawEmail || !withdrawAccount || !ktpVerified || !disclaimerChecked) {
      setError('Harap lengkapi semua verifikasi dan kolom yang diperlukan.');
      return;
    }
    setLoadingAction(true);
    setError('');

    try {
      const token = localStorage.getItem('auth_token');
      const res = await fetch(`${API_BASE_URL}/api/partnership/withdraw/submit`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          email: withdrawEmail,
          provider: withdrawProvider,
          account_number: withdrawAccount
        })
      });
      const data = await res.json();
      if (res.ok && data.success) {
        setSuccessMsg(data.message || 'Penarikan komisi berhasil diajukan!');
        setShowWithdrawModal(false);
        // Refresh dashboard data to lock unpaid balance
        fetchDashboardData();
      } else {
        setError(data.message || 'Gagal mengajukan penarikan.');
      }
    } catch (err) {
      setError('Gagal mengirim pengajuan penarikan.');
    } finally {
      setLoadingAction(false);
    }
  };

  const copyToClipboard = (text: string, isLink: boolean) => {
    navigator.clipboard.writeText(text);
    if (isLink) {
      setCopiedLink(true);
      setTimeout(() => setCopiedLink(false), 2000);
    } else {
      setCopiedCode(true);
      setTimeout(() => setCopiedCode(false), 2000);
    }
  };

  return (
    <div className="min-h-screen bg-black text-white flex">
      <Sidebar />
      <main className="flex-1 bg-[#050505] min-h-screen pt-16 md:pt-0 overflow-x-hidden">
        <TopBar 
          title={t('referral.title') || "Referral & Partnership"}
          name={userName}
          selectedAccount={selectedAccount}
          onAccountChange={setSelectedAccount}
          showAccountSwitcher={true}
          showDemoToggle={false}
        />

        <div className="mx-auto w-full max-w-[1132px] py-6 px-4 md:px-8 font-sans">
          
          {/* Action Messages */}
          {error && (
            <div className="mb-6 p-4 bg-red-950/20 border border-red-900/30 rounded-2xl flex items-center gap-3 text-red-400 text-sm">
              <AlertCircle size={20} className="shrink-0" />
              <span>{error}</span>
            </div>
          )}

          {successMsg && (
            <div className="mb-6 p-4 bg-green-950/20 border border-green-900/30 rounded-2xl flex items-center gap-3 text-green-400 text-sm">
              <CheckCircle2 size={20} className="shrink-0" />
              <span>{successMsg}</span>
            </div>
          )}

          {/* PAGE STATES */}

          {/* 1. LOADING STATE */}
          {pageState === 'loading' && (
            <div className="flex flex-col items-center justify-center py-20">
              <Loader2 size={40} className="animate-spin text-red-500 mb-4" />
              <p className="text-neutral-400 text-sm">Memuat data kemitraan Anda...</p>
            </div>
          )}

          {/* 2. PRE-APPLY CHOICE SCREEN */}
          {pageState === 'apply_pre' && (
            <motion.div 
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="space-y-8 max-w-2xl mx-auto py-8 text-center"
            >
              <div className="space-y-3">
                <div className="w-16 h-16 rounded-3xl bg-red-600/10 border border-red-600/20 flex items-center justify-center text-red-500 mx-auto">
                  <Sparkles size={28} />
                </div>
                <h1 className="text-3xl font-black tracking-tight font-sans">Kemitraan & Program Referral</h1>
                <p className="text-neutral-400 text-sm font-sans">
                  Dapatkan komisi hingga 30% berulang (recurring) dengan mengajak orang lain bergabung dengan Catat Crypto. Pilih jenis kemitraan Anda.
                </p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 text-left">
                {/* Influencer Card */}
                <div 
                  onClick={() => { setPartnerType('influencer'); setPageState('apply_form'); }}
                  className="bg-neutral-900/50 border border-neutral-800 hover:border-red-600/50 p-6 rounded-3xl cursor-pointer transition-all group relative overflow-hidden"
                >
                  <div className="absolute top-0 right-0 w-24 h-24 bg-red-600/5 rounded-full blur-[40px]" />
                  <Award size={24} className="text-red-500 mb-4" />
                  <h3 className="text-base font-black uppercase tracking-wider text-white">Influencer Program</h3>
                  <p className="text-xs text-neutral-400 mt-2 leading-relaxed font-sans">
                    Cocok untuk Anda yang memiliki channel YouTube, Twitter, TikTok, atau publikasi trading aktif. Bagikan kode personal unik.
                  </p>
                  <div className="flex items-center gap-1 text-[10px] font-black text-red-500 uppercase tracking-widest mt-4 group-hover:translate-x-1 transition-transform">
                    Pilih Program <ChevronRight size={12} />
                  </div>
                </div>

                {/* Community Card */}
                <div 
                  onClick={() => { setPartnerType('community'); setPageState('apply_form'); }}
                  className="bg-neutral-900/50 border border-neutral-800 hover:border-red-600/50 p-6 rounded-3xl cursor-pointer transition-all group relative overflow-hidden"
                >
                  <div className="absolute top-0 right-0 w-24 h-24 bg-red-600/5 rounded-full blur-[40px]" />
                  <Users size={24} className="text-red-500 mb-4" />
                  <h3 className="text-base font-black uppercase tracking-wider text-white">Community Partner</h3>
                  <p className="text-xs text-neutral-400 mt-2 leading-relaxed font-sans">
                    Untuk Signal Groups, regional trading guilds, atau komunitas edukasi. Dapatkan persentase diskon & komisi tier khusus.
                  </p>
                  <div className="flex items-center gap-1 text-[10px] font-black text-red-500 uppercase tracking-widest mt-4 group-hover:translate-x-1 transition-transform">
                    Pilih Program <ChevronRight size={12} />
                  </div>
                </div>
              </div>
            </motion.div>
          )}

          {/* 3. APPLICATION FORM SCREEN */}
          {pageState === 'apply_form' && (
            <motion.div 
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="max-w-xl mx-auto py-4"
            >
              <button 
                onClick={() => setPageState('apply_pre')}
                className="text-xs font-bold text-neutral-400 hover:text-white mb-6 flex items-center gap-1 transition-colors"
              >
                &larr; Kembali
              </button>

              <div className="bg-[#0D0D0D] border border-neutral-800 rounded-3xl p-6 md:p-8 space-y-6">
                <header>
                  <span className="text-[9px] font-black uppercase tracking-widest text-red-500 block mb-1">
                    {partnerType === 'community' ? 'Community Partnership' : 'Influencer Partnership'}
                  </span>
                  <h2 className="text-xl font-black text-white">Formulir Pendaftaran Mitra</h2>
                  <p className="text-xs text-neutral-400 mt-1">
                    Harap lengkapi kolom berikut. Tim kami akan meninjau dan mengirimkan detail persetujuan melalui email Anda.
                  </p>
                </header>

                <form onSubmit={handleApplySubmit} className="space-y-4">
                  {/* Community/Influencer Name */}
                  <div className="space-y-1">
                    <label className="text-[10px] font-bold text-neutral-500 uppercase tracking-widest pl-1">
                      {partnerType === 'community' ? 'Nama Komunitas' : 'Nama Influencer / Brand'}
                    </label>
                    <input 
                      type="text"
                      required
                      placeholder={partnerType === 'community' ? 'Crypto Cuan Group' : 'Nama Akun / Channel Anda'}
                      className="w-full bg-neutral-900 border border-neutral-800 rounded-xl py-2.5 px-4 text-sm focus:border-red-600 outline-none text-white transition-colors"
                      value={communityName}
                      onChange={(e) => setCommunityName(e.target.value)}
                    />
                  </div>

                  {/* Community Link */}
                  <div className="space-y-1">
                    <label className="text-[10px] font-bold text-neutral-500 uppercase tracking-widest pl-1">
                      Tautan Komunitas / Channel (Wajib https://)
                    </label>
                    <input 
                      type="url"
                      required
                      placeholder="https://t.me/... atau https://youtube.com/..."
                      className="w-full bg-neutral-900 border border-neutral-800 rounded-xl py-2.5 px-4 text-sm focus:border-red-600 outline-none text-white font-mono text-xs transition-colors"
                      value={communityLink}
                      onChange={(e) => setCommunityLink(e.target.value)}
                    />
                  </div>

                  {/* Apply Reason */}
                  <div className="space-y-1">
                    <label className="text-[10px] font-bold text-neutral-500 uppercase tracking-widest pl-1">
                      Alasan Kemitraan (Opsional)
                    </label>
                    <textarea 
                      placeholder="Tuliskan alasan mengapa Anda tertarik bermitra..."
                      rows={4}
                      className="w-full bg-neutral-900 border border-neutral-800 rounded-xl py-2.5 px-4 text-sm focus:border-red-600 outline-none text-white transition-colors resize-none"
                      value={applyReason}
                      onChange={(e) => setApplyReason(e.target.value)}
                    />
                  </div>

                  {/* Notification preference */}
                  <div className="space-y-2">
                    <label className="text-[10px] font-bold text-neutral-500 uppercase tracking-widest pl-1 block">
                      Preferensi Notifikasi Persetujuan
                    </label>
                    <div className="flex gap-4">
                      {['email', 'telegram'].map(pref => (
                        <button
                          key={pref}
                          type="button"
                          onClick={() => setNotifPref(pref as any)}
                          className={`px-4 py-2 rounded-xl text-xs font-bold border transition-all ${notifPref === pref ? 'bg-white text-black border-white' : 'bg-transparent border-neutral-800 text-neutral-400 hover:border-neutral-700'}`}
                        >
                          {pref.toUpperCase()}
                        </button>
                      ))}
                    </div>
                  </div>

                  <button 
                    type="submit"
                    disabled={loadingAction}
                    className="w-full py-3.5 bg-red-600 hover:bg-red-500 text-white font-bold rounded-xl transition-all flex items-center justify-center gap-2 cursor-pointer disabled:opacity-50 mt-6 font-sans"
                  >
                    {loadingAction ? <Loader2 size={16} className="animate-spin" /> : 'Kirim Pengajuan Kemitraan'}
                  </button>
                </form>
              </div>
            </motion.div>
          )}

          {/* 4. PENDING SCREEN */}
          {pageState === 'pending' && (
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="max-w-md mx-auto py-12 text-center space-y-6"
            >
              <div className="w-16 h-16 rounded-3xl bg-amber-600/10 border border-amber-600/20 flex items-center justify-center text-amber-500 mx-auto">
                <RefreshCw size={28} className="animate-spin" style={{ animationDuration: '3s' }} />
              </div>
              <div className="space-y-2">
                <h2 className="text-2xl font-black text-white">Pengajuan Sedang Ditinjau</h2>
                <p className="text-neutral-400 text-xs leading-relaxed max-w-sm mx-auto font-sans">
                  Pengajuan Kemitraan Anda berhasil dikirim ke tim marketing. Kami akan meninjau kelayakan komunitas Anda dan mengirim notifikasi persetujuan via Email/Telegram.
                </p>
              </div>
              <button 
                onClick={fetchDashboardData}
                className="px-6 py-2.5 bg-neutral-900 border border-neutral-800 hover:border-neutral-700 rounded-xl text-xs font-bold transition-all"
              >
                Cek Status Terbaru
              </button>
            </motion.div>
          )}

          {/* 5. REJECTED SCREEN */}
          {pageState === 'rejected' && (
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="max-w-md mx-auto py-12 text-center space-y-6"
            >
              <div className="w-16 h-16 rounded-3xl bg-red-600/10 border border-red-600/20 flex items-center justify-center text-red-500 mx-auto">
                <AlertTriangle size={28} />
              </div>
              <div className="space-y-2">
                <h2 className="text-2xl font-black text-white">Pengajuan Ditangguhkan</h2>
                <p className="text-neutral-400 text-xs leading-relaxed max-w-sm mx-auto font-sans">
                  Maaf, pengajuan kemitraan komunitas Anda belum memenuhi standar kami saat ini. Silakan kembangkan komunitas Anda dan ajukan kemitraan kembali nanti.
                </p>
              </div>
              <button 
                onClick={() => setPageState('apply_pre')}
                className="px-6 py-2.5 bg-red-600 hover:bg-red-500 text-white rounded-xl text-xs font-bold transition-all shadow-lg"
              >
                Ajukan Ulang
              </button>
            </motion.div>
          )}

          {/* 6. PARTNER DASHBOARD SCREEN */}
          {pageState === 'dashboard' && partnershipData && (
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="space-y-8"
            >
              {/* Promotion community upgrade banner */}
              {partnershipData.show_community_upgrade_prompt && (
                <div className="p-4 bg-gradient-to-r from-purple-950/80 to-neutral-950 border border-purple-800/40 rounded-3xl flex flex-col sm:flex-row items-center justify-between gap-4">
                  <div className="flex items-center gap-3 text-left">
                    <div className="w-10 h-10 rounded-2xl bg-purple-600/15 border border-purple-500/30 flex items-center justify-center text-purple-400 shrink-0">
                      <Award size={18} />
                    </div>
                    <div>
                      <h5 className="text-sm font-black text-white">Tingkatkan ke Kemitraan Komunitas!</h5>
                      <p className="text-[11px] text-purple-300 mt-0.5 font-sans">
                        Anda telah mengundang lebih dari 30 pengguna. Daftarkan komunitas Anda untuk komisi lebih besar (hingga 30%).
                      </p>
                    </div>
                  </div>
                  <button 
                    onClick={() => { setPartnerType('community'); setPageState('apply_form'); }}
                    className="px-4 py-2 bg-purple-600 hover:bg-purple-500 text-white text-[10px] font-black uppercase tracking-widest rounded-xl transition-all shrink-0"
                  >
                    Daftar Sekarang
                  </button>
                </div>
              )}

              {/* Referral details cards */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                
                {/* Code & Link Card */}
                <div className="md:col-span-2 bg-[#0D0D0D] border border-neutral-800 rounded-3xl p-6 relative overflow-hidden flex flex-col justify-between">
                  <div className="absolute top-0 right-0 w-32 h-32 bg-red-600/5 rounded-full blur-[40px] pointer-events-none" />
                  
                  <div>
                    <span className="text-[9px] font-black text-neutral-500 uppercase tracking-widest block">Undangan Anda</span>
                    <h3 className="text-lg font-black text-white mt-1">Bagikan Kode Referral</h3>
                    <p className="text-xs text-neutral-400 mt-1 max-w-md leading-relaxed font-sans">
                      Bagikan kode atau tautan rujukan Anda ke teman. Mereka mendapat bonus/diskon trial, dan Anda mendapat komisi {partnershipData.commission_percent}% recurring dari pembayaran mereka.
                    </p>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-6">
                    {/* Code Copy */}
                    <div className="bg-neutral-900 border border-neutral-800 rounded-2xl p-3.5 flex items-center justify-between">
                      <div>
                        <span className="text-[8px] font-bold text-neutral-500 uppercase tracking-widest block">Kode Referral</span>
                        <span className="text-sm font-mono font-black text-white tracking-widest">{partnershipData.referral_code}</span>
                      </div>
                      <button 
                        onClick={() => copyToClipboard(partnershipData.referral_code, false)}
                        className="p-2 hover:bg-neutral-800 text-neutral-400 hover:text-white rounded-xl transition-colors cursor-pointer"
                      >
                        {copiedCode ? <Check size={16} className="text-green-500" /> : <Copy size={16} />}
                      </button>
                    </div>

                    {/* Link Copy */}
                    <div className="bg-neutral-900 border border-neutral-800 rounded-2xl p-3.5 flex items-center justify-between">
                      <div className="min-w-0 flex-1">
                        <span className="text-[8px] font-bold text-neutral-500 uppercase tracking-widest block">Link Referral</span>
                        <span className="text-xs font-mono text-neutral-400 truncate block">{partnershipData.referral_link}</span>
                      </div>
                      <button 
                        onClick={() => copyToClipboard(partnershipData.referral_link, true)}
                        className="p-2 hover:bg-neutral-800 text-neutral-400 hover:text-white rounded-xl transition-colors cursor-pointer ml-2"
                      >
                        {copiedLink ? <Check size={16} className="text-green-500" /> : <Copy size={16} />}
                      </button>
                    </div>
                  </div>
                </div>

                {/* Balance & Withdraw Card */}
                <div className="bg-[#0D0D0D] border border-neutral-800 rounded-3xl p-6 relative overflow-hidden flex flex-col justify-between">
                  <div className="absolute -bottom-12 -left-12 w-32 h-32 bg-red-600/5 rounded-full blur-[40px] pointer-events-none" />
                  
                  <div>
                    <span className="text-[9px] font-black text-neutral-500 uppercase tracking-widest block">Saldo Komisi</span>
                    <h2 className="text-3xl font-black text-red-500 mt-2 font-mono">
                      IDR {partnershipData.summary.unpaid_commission_idr.toLocaleString('id-ID')}
                    </h2>
                    <p className="text-[10px] text-neutral-400 mt-2 font-sans">
                      Total komisi terkumpul: <span className="text-white font-mono">IDR {partnershipData.summary.total_commission_idr.toLocaleString('id-ID')}</span>
                    </p>
                  </div>

                  <div className="mt-6 space-y-2">
                    {/* Withdrawal Lock warning if balance < 100K */}
                    {partnershipData.summary.unpaid_commission_idr < 100000 && (
                      <div className="text-[10px] text-neutral-500 bg-neutral-900/50 border border-neutral-800 p-2 rounded-xl flex items-center gap-1.5 justify-center font-sans">
                        <Info size={12} className="shrink-0 text-neutral-400" />
                        <span>Min. penarikan adalah IDR 100.000</span>
                      </div>
                    )}
                    
                    <button 
                      onClick={() => {
                        setWithdrawEmail(localStorage.getItem('user_email') || '');
                        setKtpVerified(false);
                        setDisclaimerChecked(false);
                        setExpectedName('');
                        setKtpError('');
                        setKtpSuccessMsg('');
                        setShowWithdrawModal(true);
                      }}
                      disabled={partnershipData.summary.unpaid_commission_idr < 100000}
                      className="w-full py-3 bg-red-600 hover:bg-red-500 disabled:bg-neutral-900 disabled:text-neutral-500 disabled:border disabled:border-neutral-800 disabled:cursor-not-allowed text-white font-bold text-xs rounded-xl transition-all shadow-lg active:scale-[0.98] flex items-center justify-center gap-1.5 cursor-pointer font-sans"
                    >
                      <Wallet size={14} />
                      Tarik Komisi
                    </button>
                  </div>
                </div>
              </div>

              {/* Stats Summary Section */}
              <div className={`grid grid-cols-2 ${partnershipData.is_community ? 'sm:grid-cols-4' : 'sm:grid-cols-3'} gap-4 font-sans`}>
                <div className="bg-[#0D0D0D] border border-neutral-800 rounded-2xl p-4 text-center">
                  <span className="text-[8px] font-black text-neutral-500 uppercase tracking-widest block">{t('referral.totalReferrals') || "Total Rujukan"}</span>
                  <span className="text-2xl font-black text-white mt-1 block font-mono">{partnershipData.summary.total_referrals}</span>
                  <span className="text-[9px] text-neutral-400 mt-1 block">{t('referral.registeredAccounts') || "Akun Terdaftar"}</span>
                </div>
                <div className="bg-[#0D0D0D] border border-neutral-800 rounded-2xl p-4 text-center">
                  <span className="text-[8px] font-black text-neutral-500 uppercase tracking-widest block">{t('referral.activeSubscribers') || "Langganan Aktif"}</span>
                  <span className="text-2xl font-black text-white mt-1 block font-mono">{partnershipData.summary.active_subscribers}</span>
                  <span className="text-[9px] text-neutral-400 mt-1 block">{t('referral.activeSubscribersSub') || "Pro & Premium"}</span>
                </div>
                <div className="bg-[#0D0D0D] border border-neutral-800 rounded-2xl p-4 text-center">
                  <span className="text-[8px] font-black text-neutral-500 uppercase tracking-widest block">{t('referral.partnershipStatus') || "Status Kemitraan"}</span>
                  <span className="text-sm font-black text-white mt-2.5 block uppercase tracking-wider">
                    {partnershipData.is_community ? (t('referral.communityPartner') || 'KOMUNITAS') : (t('referral.personal') || 'PERSONAL')}
                  </span>
                </div>
                {partnershipData.is_community && (
                  <div className="bg-[#0D0D0D] border border-neutral-800 rounded-2xl p-4 text-center">
                    <span className="text-[8px] font-black text-neutral-500 uppercase tracking-widest block">{t('referral.tierLevel') || "Tier Level"}</span>
                    <span className="text-sm font-black text-red-500 mt-2.5 block tracking-widest flex items-center justify-center gap-1">
                      <Award size={14} />
                      {partnershipData.summary.tier || 'Basic'}
                    </span>
                  </div>
                )}
              </div>

              {/* Commissions Tracker Log Table */}
              <div className="bg-[#0D0D0D] border border-neutral-800 rounded-3xl p-6 font-sans">
                <h3 className="text-base font-black text-white mb-4">Komisi Tracker</h3>
                
                {commissions.length === 0 ? (
                  <div className="text-center py-10">
                    <Users size={32} className="text-neutral-600 mx-auto mb-2" />
                    <p className="text-xs text-neutral-500">Belum ada data komisi tercatat.</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-left text-xs">
                      <thead>
                        <tr className="border-b border-neutral-800 text-neutral-500 font-bold uppercase tracking-wider">
                          <th className="pb-3 pl-2">Tanggal</th>
                          <th className="pb-3">User (Disamarkan)</th>
                          <th className="pb-3">Email (Disamarkan)</th>
                          <th className="pb-3">Status</th>
                          <th className="pb-3 pr-2 text-right">Jumlah Komisi</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-neutral-900">
                        {commissions.map((log) => (
                          <tr key={log.id} className="hover:bg-neutral-900/30 transition-colors">
                            <td className="py-3.5 pl-2 text-neutral-400 font-mono">
                              {new Date(log.date).toLocaleDateString('id-ID', { year: 'numeric', month: 'short', day: 'numeric' })}
                            </td>
                            <td className="py-3.5 font-bold text-white">{log.referred_user_name}</td>
                            <td className="py-3.5 text-neutral-400 font-mono">{log.referred_user_email}</td>
                            <td className="py-3.5">
                              <span className={`px-2 py-0.5 rounded-full text-[9px] font-black uppercase tracking-wider ${
                                log.status === 'paid' ? 'bg-green-600/10 text-green-400 border border-green-500/20' :
                                log.status === 'earned' ? 'bg-blue-600/10 text-blue-400 border border-blue-500/20' :
                                log.status === 'pending' ? 'bg-amber-600/10 text-amber-400 border border-amber-500/20' :
                                'bg-neutral-800 text-neutral-500 border border-neutral-700'
                              }`}>
                                {log.status}
                              </span>
                            </td>
                            <td className="py-3.5 pr-2 text-right font-black text-white font-mono">
                              IDR {log.amount_idr.toLocaleString('id-ID')}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </motion.div>
          )}
        </div>
      </main>

      {/* WITHDRAWAL MODAL */}
      <AnimatePresence>
        {showWithdrawModal && (
          <>
            {/* Backdrop */}
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => {
                if (!isVerifyingKtp) setShowWithdrawModal(false);
              }}
              className="fixed inset-0 bg-black/85 backdrop-blur-md z-[10000] cursor-pointer"
            />

            {/* Modal Box */}
            <motion.div 
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="fixed inset-0 z-[10001] flex items-center justify-center p-4"
            >
              <div className="bg-neutral-950 border border-neutral-800 rounded-3xl p-6 md:p-8 max-w-lg w-full relative shadow-2xl overflow-y-auto max-h-[90vh] space-y-6 font-sans">
                <button 
                  onClick={() => setShowWithdrawModal(false)}
                  disabled={isVerifyingKtp}
                  className="absolute top-4 right-4 text-neutral-500 hover:text-white transition-colors disabled:opacity-30 cursor-pointer"
                >
                  <X size={20} />
                </button>

                <header>
                  <span className="text-[9px] font-black uppercase tracking-widest text-red-500 block mb-1">Pencairan Dana</span>
                  <h3 className="text-xl font-black text-white">Form Pengajuan Penarikan Komisi</h3>
                  <p className="text-xs text-neutral-400 mt-1">
                    Silakan isi rekening penerima dan verifikasi identitas Anda untuk otorisasi dana.
                  </p>
                </header>

                {ktpError && (
                  <div className="p-3 bg-red-900/20 border border-red-900/40 rounded-xl text-red-400 text-xs flex items-center gap-2">
                    <AlertCircle size={16} className="shrink-0" />
                    <span>{ktpError}</span>
                  </div>
                )}

                {ktpSuccessMsg && (
                  <div className="p-3 bg-green-950/20 border border-green-900/40 rounded-xl text-green-400 text-xs flex items-center gap-2">
                    <CheckCircle2 size={16} className="shrink-0" />
                    <span>{ktpSuccessMsg}</span>
                  </div>
                )}

                <form onSubmit={handleWithdrawSubmit} className="space-y-4 text-left">
                  {/* Account Name */}
                  <div className="space-y-1">
                    <label className="text-[10px] font-bold text-neutral-500 uppercase tracking-widest pl-1">
                      Nama Pemilik Rekening / Bank Account Name
                    </label>
                    <input 
                      type="text" 
                      required
                      placeholder="Contoh: Budi Santoso"
                      disabled={ktpVerified || isVerifyingKtp}
                      className="w-full bg-neutral-900 border border-neutral-800 rounded-xl py-2.5 px-4 text-sm focus:border-red-600 outline-none text-white transition-all disabled:opacity-50"
                      value={expectedName}
                      onChange={(e) => setExpectedName(e.target.value)}
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    {/* Provider */}
                    <div className="space-y-1">
                      <label className="text-[10px] font-bold text-neutral-500 uppercase tracking-widest pl-1">
                        Bank / E-Wallet
                      </label>
                      <select 
                        disabled={isVerifyingKtp}
                        className="w-full bg-neutral-900 border border-neutral-800 rounded-xl py-2.5 px-4 text-sm focus:border-red-600 outline-none text-white transition-colors"
                        value={withdrawProvider}
                        onChange={(e) => setWithdrawProvider(e.target.value)}
                      >
                        <option>BCA</option>
                        <option>Mandiri</option>
                        <option>BRI</option>
                        <option>DANA</option>
                        <option>GOPAY</option>
                        <option>OVO</option>
                      </select>
                    </div>

                    {/* Account Number */}
                    <div className="space-y-1">
                      <label className="text-[10px] font-bold text-neutral-500 uppercase tracking-widest pl-1">
                        Nomor Rekening / HP
                      </label>
                      <input 
                        type="text" 
                        required
                        placeholder="1234567890"
                        disabled={isVerifyingKtp}
                        className="w-full bg-neutral-900 border border-neutral-800 rounded-xl py-2.5 px-4 text-sm focus:border-red-600 outline-none text-white font-mono transition-colors"
                        value={withdrawAccount}
                        onChange={(e) => setWithdrawAccount(e.target.value.replace(/\D/g, ''))}
                      />
                    </div>
                  </div>

                  {/* Email */}
                  <div className="space-y-1">
                    <label className="text-[10px] font-bold text-neutral-500 uppercase tracking-widest pl-1">
                      Email Tujuan Laporan
                    </label>
                    <input 
                      type="email" 
                      required
                      disabled={isVerifyingKtp}
                      className="w-full bg-neutral-900 border border-neutral-800 rounded-xl py-2.5 px-4 text-sm focus:border-red-600 outline-none text-white font-mono transition-colors"
                      value={withdrawEmail}
                      onChange={(e) => setWithdrawEmail(e.target.value)}
                    />
                  </div>

                  {/* KTP OCR VERIFICATION CARD */}
                  <div className="bg-[#111] border border-neutral-800 rounded-2xl p-4 space-y-3 relative overflow-hidden">
                    <div className="flex items-start justify-between">
                      <div>
                        <span className="text-[9px] font-black text-neutral-500 uppercase tracking-widest">Langkah Verifikasi</span>
                        <h4 className="text-xs font-bold text-white mt-0.5">Unggah Foto KTP Anda (Verifikasi OCR Nera)</h4>
                        <p className="text-[10px] text-neutral-500 mt-0.5 leading-relaxed">
                          AI akan mencocokkan Nama Pemilik KTP dengan Nama Pemilik Rekening di atas. File KTP dihapus dari RAM setelah verifikasi selesai (Zero-Storage Compliance).
                        </p>
                      </div>
                      <div className={`w-2.5 h-2.5 rounded-full shrink-0 mt-1 ${ktpVerified ? 'bg-green-500 animate-pulse' : 'bg-red-500'}`} />
                    </div>

                    <div className="flex items-center gap-3">
                      <label className={`flex items-center gap-2 px-4 py-2.5 rounded-xl border border-neutral-800 bg-neutral-900 text-neutral-400 hover:border-red-600/50 hover:text-white transition-all text-xs font-bold ${ktpVerified || isVerifyingKtp ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}>
                        <Upload size={14} />
                        Pilih Gambar KTP
                        <input 
                          type="file" 
                          accept="image/*"
                          className="hidden"
                          disabled={ktpVerified || isVerifyingKtp || !expectedName.trim()}
                          onChange={handleKtpUpload}
                        />
                      </label>

                      {isVerifyingKtp && (
                        <div className="flex items-center gap-1.5 text-[10px] font-bold text-amber-500">
                          <Loader2 size={12} className="animate-spin" />
                          <span>Nera AI memindai KTP...</span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Disclaimer Checkbox */}
                  <div className="flex items-start gap-2.5 pt-2">
                    <input 
                      type="checkbox"
                      id="disclaimer"
                      disabled={isVerifyingKtp}
                      className="mt-0.5 cursor-pointer accent-red-600 rounded"
                      checked={disclaimerChecked}
                      onChange={(e) => setDisclaimerChecked(e.target.checked)}
                    />
                    <label htmlFor="disclaimer" className="text-[10px] text-neutral-400 leading-normal select-none cursor-pointer">
                      Saya menyatakan nomor rekening/e-wallet yang saya masukkan sudah benar dan memahami bahwa kesalahan ketik sepenuhnya merupakan tanggung jawab saya.
                    </label>
                  </div>

                  <button 
                    type="submit"
                    disabled={loadingAction || isVerifyingKtp || !disclaimerChecked || !ktpVerified || !withdrawAccount || !withdrawEmail}
                    className="w-full py-3.5 bg-red-600 hover:bg-red-500 disabled:bg-neutral-900 disabled:text-neutral-500 disabled:border disabled:border-neutral-850 disabled:cursor-not-allowed text-white font-bold rounded-xl transition-all flex items-center justify-center gap-2 cursor-pointer disabled:opacity-50 mt-6"
                  >
                    {loadingAction ? <Loader2 size={16} className="animate-spin" /> : 'Kirim Pengajuan Penarikan'}
                  </button>
                </form>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
};

export default ReferralPage;
